import React, { useState, useEffect } from "react";
import { useDebounce } from "./useDebounce";

/**
 * A debounced input component that wraps a standard HTML input.
 * It maintains its own local state for immediate visual feedback
 * and calls the `onChange` callback only after the specified `debounce` delay.
 */
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState(initialValue);
  const debouncedValue = useDebounce(value, debounce);
  const prevDebouncedValue = React.useRef(debouncedValue);

  // Sync local state with external value if it changes externally
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // Handle the debounce logic via the hook
  useEffect(() => {
    if (debouncedValue !== prevDebouncedValue.current) {
      prevDebouncedValue.current = debouncedValue;
      if (debouncedValue !== initialValue) {
        onChange(debouncedValue);
      }
    }
  }, [debouncedValue, onChange, initialValue]);

  return (
    <input
      {...props}
      value={value ?? ""}
      onChange={(e) => setValue(e.target.value)}
    />
  );
};

export default DebouncedInput;